export const QUERY_STALE_TIMES = {
  categories: 5 * 60 * 1000, // 5 minutes
  transactions: 2 * 60 * 1000, // 2 minutes
  reports: 5 * 60 * 1000, // 5 minutes
  budgetGoals: 5 * 60 * 1000, // 5 minutes
  budgetSummary: 5 * 60 * 1000, // 5 minutes
} as const;

export const QUERY_KEYS = {
  categories: 'categories',
  transactions: 'transactions',
  reports: 'reports',
  budgetGoals: 'budget-goals',
  budgetSummary: 'budget-summary',
} as const;

export const PAGINATION = {
  defaultPageSize: 10,
  pageSizeOptions: [10, 25, 50, 100],
} as const;

export const DATE_RANGES = {
  defaultDaysBack: 30,
  maxDaysBack: 365,
} as const;

export const TRANSACTION_TYPES = {
  income: 'income',
  expense: 'expense',
} as const;

export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  income: 'Receita',
  expense: 'Despesa',
} as const;

export const CURRENCY = {
  locale: 'pt-BR',
  currency: 'BRL',
} as const;

export const DATE_LOCALE = 'pt-BR';

export const STORAGE_KEYS = {
  idToken: 'idToken',
  user: 'user',
  theme: 'theme',
} as const;

// The Categorias filter that narrows the list to unclassified categories; Orçamento's warning links to it.
export const CATEGORY_PENDING_FILTER = { param: 'classification', value: 'pending' } as const;

// Painel stacks two readings of one period — the cash flow (any range) and the budget (one month). Each section
// head is also its anchor, so a link can land on the budget: `/dashboard#budget`.
export const DASHBOARD_SECTIONS = { cashFlow: 'cash-flow', budget: 'budget' } as const;

// Paths, search params and anchors are English; only what is shown on screen is pt-BR.
export const ROUTES = {
  home: '/',
  login: '/login',
  signup: '/signup',
  forgotPassword: '/forgot-password',
  dashboard: '/dashboard',
  dashboardBudget: `/dashboard#${DASHBOARD_SECTIONS.budget}`,
  transactions: '/transactions',
  transactionsAdd: '/transactions/add',
  categories: '/categories',
  categoriesPending: `/categories?${CATEGORY_PENDING_FILTER.param}=${CATEGORY_PENDING_FILTER.value}`,
  goals: '/goals',
} as const;

export const API_TIMEOUT = 30000; // 30 seconds

export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
} as const;


// src/lib/constants.ts
export const BUDGET_TYPES = [
  'fixed-expenses', 'comfort', 'goals', 'pleasure', 'financial-freedom', 'knowledge',
] as const;
export const BUDGET_TREATMENTS = [
  'income', 'spend', 'refund', 'earmark', 'deduction', 'excluded',
] as const;
export type BudgetType = (typeof BUDGET_TYPES)[number];
export type BudgetTreatment = (typeof BUDGET_TREATMENTS)[number];

// An Orçamento row leads to the categories that fed it: Categorias narrowed to one group, carrying the month
// (`period=2026-09`) so each category's own way into Transações reads that same month.
export const CATEGORY_GROUP_PARAM = 'group';
export const categoriesOfGroup = (group: BudgetType, period?: string): string => {
  const params = new URLSearchParams({ [CATEGORY_GROUP_PARAM]: group });
  if (period) params.set('period', period);
  return `${ROUTES.categories}?${params}`;
};
export const transactionsOfCategory = (categoryId: string, period?: string): string => {
  const params = new URLSearchParams({ category: categoryId });
  if (period) params.set('period', period);
  return `${ROUTES.transactions}?${params}`;
};

// The six goals must sum to exactly 100%. Compared in hundredths of a percent (10000), never as floats.
export const BUDGET_GOALS_TOTAL_HUNDREDTHS = 10000;
